import {create} from "zustand";
import {EntityType, Office} from "./Model";
import {CrudRestResource, RestResource} from "./RestResource";
import {ArrayResourceStoreBuilder, EntityStore, refreshStoreFn} from "./resourceStoreBuilder";
import type {StateCreator, StoreApi} from "zustand/vanilla";
import {Location} from "react-router-dom";
import {useAuthStore} from "../app-layout/authStore";
import Permission from "../util/access/Permission";
import {rolesOf} from "../util/access/AccessRules";
import {AccessibleOffices} from "../util/access/AccessibleOffices";
import {onRoleChange} from "../util/access/onRoleChange";
import {Access} from "../util/access/Access";


export function replaceOfficePathPrefix(location: Location, newOfficePathElement: string): string {
    const pathSegments = location.pathname.split('/');
    if (pathSegments.length >= 3 && pathSegments[1] === 'offices') {
        // If the path is /offices/:officePathEl[/...] then update the office path element
        pathSegments[2] = newOfficePathElement;
        const newPath = pathSegments.join('/');
        return `${newPath}${location.search}${location.hash}`;
    } else {
        return `/offices/${newOfficePathElement}${location.pathname}${location.search}${location.hash}`;
    }
}

const officePathRegex = /^\/offices\/(?<pathEl>([^/]+-)?(?<id>\d+))(?<tail>(\/.*)?)$/;
function getCurrentOfficeIdFromLocation(pathname: string): { officeId: number, officePathElement: string } | null {
    const matchResult = officePathRegex.exec(pathname);
    if (matchResult && matchResult.groups) {
        const officePathElement = matchResult.groups.pathEl;
        const officeId = parseInt(matchResult.groups.id);
        return {officeId, officePathElement};
    } else {
        return null;
    }
}

function redirectToDefaultOffice() {
    const user = useAuthStore.getState().user;
    const defaultOfficePath = new AccessibleOffices(rolesOf(user)).getDefaultOfficePath();
    if (defaultOfficePath) {
        const newPath = replaceOfficePathPrefix(window.location as unknown as Location, defaultOfficePath);
        window.location.replace(newPath);
    }
}

interface CurrentOfficeStore {
    officeId: Office['id'] | null;
    officePathElement: string | null;
    onPathChange(pathname: string): void;
}

export const useCurrentOfficeStore = create<CurrentOfficeStore>((set, get) => {
    const currentOffice = getCurrentOfficeIdFromLocation(window.location.pathname);
    if (!currentOffice) {
        redirectToDefaultOffice();
    }

    onRoleChange(() => {
        const {officeId: prevOfficeId} = get();
        if (prevOfficeId === null) {
            redirectToDefaultOffice();
        }
    })

    return {
        officeId: currentOffice?.officeId || null,
        officePathElement: currentOffice?.officePathElement || null,
        onPathChange: (pathname: string) => {
            const newOffice = getCurrentOfficeIdFromLocation(pathname);
            const {officeId: prevOfficeId, officePathElement: prevOfficePathElement} = get();
            if (newOffice && (newOffice.officeId !== prevOfficeId || newOffice.officePathElement !== prevOfficePathElement)) {
                set({officeId: newOffice.officeId, officePathElement: newOffice.officePathElement});
            }
        }
    };
});

export class OfficeArrayResourceStoreBuilder<Entity, Store extends EntityStore<Entity[]>> extends ArrayResourceStoreBuilder<Entity, Store> {
    constructor(
        setState: StoreApi<Store>['setState'],
        readonly officeId: Office['id'] | null,
    ) {
        super(setState);
    }

    rest(path: string): RestResource<Entity[]> {
        return super.rest(`/offices/${this.officeId}${path}`);
    }

    crud<Form>(path: string, idKey: keyof Entity): CrudRestResource<Form, Entity, Entity[]> {
        return super.crud(`/offices/${this.officeId}${path}`, idKey);
    }

    allowEagerFetching(requiredPermission: Permission): boolean {
        if (this.officeId === null) return false;
        return new Access(useAuthStore.getState().user, this.officeId).has(requiredPermission);
    }

    updateHandle(entity: EntityType): EntityStore<Entity[]>['updateHandle'] {
        return {entity, officeId: this.officeId};
    }
}

export function officeArrayResource<Entity, Store extends EntityStore<Entity[]>>(
    entityType: EntityType,
    build: (
        builder: OfficeArrayResourceStoreBuilder<Entity, EntityStore<Entity[]>>,
        setState: StoreApi<Store>['setState'],
        getState: StoreApi<Store>['getState'],
    ) => Omit<Store, 'refresh' | 'updateHandle'>,
): StateCreator<Store> {
    return (set, get) => {

        useCurrentOfficeStore.subscribe((officeStore, prevOfficeStore) => {
            if (prevOfficeStore.officeId !== officeStore.officeId) {
                const newBuilder = new OfficeArrayResourceStoreBuilder<Entity, Store>(set, officeStore.officeId);
                set({
                    updateHandle: newBuilder.updateHandle(entityType),
                    ...build(newBuilder, set, get)
                } as Partial<Store>);
            }
        });

        const builder = new OfficeArrayResourceStoreBuilder<Entity, Store>(set, useCurrentOfficeStore.getState().officeId);
        return {
            refresh: refreshStoreFn(set, get),
            updateHandle: builder.updateHandle(entityType),
            ...build(builder, set, get),
        } as Store;
    }
}
